import { createSelector } from "reselect";

import {
  both,
  complement,
  map,
  mergeDeepLeft,
  pick,
  intersection,
  filter,
  propEq,
  propOr,
  prop,
  sort,
  sortBy,
  includes,
  find,
  findIndex,
  toLower,
  omit,
  isEmpty,
  reduce,
  pipe,
  chain, // alias for flatMap
  groupBy,
  pathOr,
  values,
  add,
  length,
  intersperse,
  lensPath,
  set,
  last,
  view,
  over,
  append,
  init
} from "ramda";

import {
  selectDisplayedPages,
  selectDisplayedPagesOnRightSidebar
} from "~/store/selectors/page";
import { selectCatsort } from "~/store/ducks/domain/config";
import { selectMaterials as selectDlsMaterials } from "~/store/ducks/application/materials/dlsMaterials";
import { selectMaterials as selectPoolMaterials } from "~/store/ducks/application/materials/materialPool";
import { selectNotes } from "~/store/ducks/user/notes";
import {
  selectPresentationMarkers,
  selectPresentationMarkersLookup
} from "~/store/ducks/user/presentationMarkers";
import {
  selectDlsActiveTab,
  selectDlsActivePageCategory,
  selectDlsActiveBookCategory,
  selectUsersettingsDlsFilters
} from "~/store/ducks/user/uistate";

import { selectDlsUserMaterials } from "~/store/ducks/user/dlsUserMaterials";

import {
  relativePageToAbsolute,
  documentsToDictionary,
  unnestAll,
  isEqualNoCase
} from "~/utils";

import Category from "~/libs/category";

import { selectShowPresentationMode } from "~/store/ducks/domain/ui/dls";

const NON_ASSIGNED_PAGE = -2;

function subCategoriesPathToNested(path) {
  const nestedCategories = path.reduceRight(
    (categories, category) => {
      return {
        category: {},
        materialReferences: [],
        subCategories: {
          [category]: categories
        }
      };
    },
    {
      category: {},
      materialReferences: [],
      subCategories: {}
    }
  );

  return prop("subCategories", nestedCategories);
}

export const selectDlsMaterialsLookup = createSelector(
  selectDlsMaterials,
  documentsToDictionary
);

export const selectPoolMaterialsLookup = createSelector(
  selectPoolMaterials,
  documentsToDictionary
);

export const selectMergedMaterials = createSelector(
  selectDlsMaterialsLookup,
  selectPoolMaterialsLookup,
  selectDlsUserMaterials,
  selectPresentationMarkersLookup,
  selectUsersettingsDlsFilters,
  (
    dlsMap,
    poolMap,
    dlsUserMaterials,
    presentationMarkers,
    usersettingsfilters
  ) => {
    // Merge dls and pool materials only by the keys of the dls materials (without design doc etc.)
    const filteredPoolMap = pick(Object.keys(dlsMap), poolMap);

    if (isEmpty(dlsMap) || isEmpty(filteredPoolMap)) {
      return [];
    }

    dlsMap = map(
      dls => ({
        ...dls,
        categories: map(
          category => ({
            ...category,
            title: category.title
              .split(">")
              .map(str => str.trim())
              .join(">")
          }),
          dls.categories || []
        )
      }),
      dlsMap
    );

    let mergedMaterials = mergeDeepLeft(dlsMap, filteredPoolMap);
    mergedMaterials = filter(material => {
      return usersettingsfilters.every(
        filter =>
          typeof filter.filter === "function" &&
          filter.filter(filter.active, material)
      );
    }, mergedMaterials);
    mergedMaterials = mergeDeepLeft(mergedMaterials, dlsUserMaterials);

    return mergeDeepLeft(mergedMaterials, presentationMarkers);
  }
);

const selectCategoryOrder = createSelector(selectCatsort, map(map(toLower)));

const selectFixedCategoryOrder = createSelector(
  selectDlsActiveTab,
  selectCategoryOrder,
  (tab, order) => {
    let fixedOrder = [];

    if (tab === "all") {
      fixedOrder = propOr([], "book", order);
    } else if (tab === "page") {
      fixedOrder = propOr([], "pages", order);
    }

    return fixedOrder;
  }
);

export const selectStaticCategories = createSelector(
  selectShowPresentationMode,
  showPresentationMode => {
    const customMaterialCategories = {
      "Eigene Materialien_page_list": {
        category: {
          isStatic: true,
          kind: "custom_materials",
          title: "Eigene Materialien",
          type: "page",
          view: "list"
        },
        materialReferences: []
      },
      "Eigene Materialien_book_list": {
        category: {
          isStatic: true,
          kind: "custom_materials",
          title: "Eigene Materialien",
          type: "book",
          view: "list"
        },
        materialReferences: []
      }
    };

    const presentationModeCategories = {
      Präsentationsmodus_page_list: {
        category: {
          isStatic: true,
          kind: "presentation_materials",
          title: "Präsentationsmodus",
          type: "page",
          view: "list"
        },
        materialReferences: []
      },
      Präsentationsmodus_book_list: {
        category: {
          isStatic: true,
          kind: "presentation_materials",
          title: "Präsentationsmodus",
          type: "book",
          view: "list"
        },
        materialReferences: []
      }
    };

    if (showPresentationMode) {
      return {
        ...customMaterialCategories,
        ...presentationModeCategories
      };
    }

    return customMaterialCategories;
  }
);

const ensureParentCategories = (
  categories,
  category,
  subCategories,
  subCategoryTitles
) => {
  if (subCategories.length <= 1) return categories;

  let parentCategoryTitle = last(init(subCategoryTitles));

  const isSingle = /\*$/.test(parentCategoryTitle);
  if (isSingle) {
    parentCategoryTitle = parentCategoryTitle.replace(/\*$/, "");
  }

  const parentCategoryFullTitle = init(subCategoryTitles).join(">");
  const parentCategoryPath = intersperse("subCategories", init(subCategories));
  const parentCategoryCategoryPath = [...parentCategoryPath, "category"];
  const parentCategoryLens = lensPath(parentCategoryCategoryPath);

  if (isEmpty(view(parentCategoryLens, categories))) {
    categories = set(
      parentCategoryLens,
      {
        title: parentCategoryTitle,
        fullTitle: parentCategoryFullTitle,
        type: category.type,
        view: isSingle ? "single" : "list"
      },
      categories
    );
  }

  return ensureParentCategories(
    categories,
    category,
    init(subCategories),
    init(subCategoryTitles)
  );
};

export const selectCategoriesWithMaterials = createSelector(
  selectMergedMaterials,
  selectShowPresentationMode,
  (materials, showPresentationMode) => {
    const materialsList = Object.values(materials);

    const categoriesWithMaterials = pipe(
      chain(material => {
        const categories = propOr([], "categories", material);

        return categories.map(category => {
          return {
            material: omit(["category", "categories"])(material),
            category
          };
        });
      }),
      reduce(
        (categories, { category, material }) => {
          const id = Category.getId(category);

          if (category.title.includes(">")) {
            const subCategoryTitles = category.title
              .split(">")
              .map(str => str.trim());
            const subCategoryTitle = last(subCategoryTitles);
            const subCategories = subCategoryTitles
              .map((title, i) => {
                const isSingle = /\*$/.test(title);
                const last = i === subCategoryTitles.length - 1;
                return {
                  ...category,
                  title: title.replace(/\*$/, ""),
                  view: last ? category.view : isSingle ? "single" : "list"
                };
              })
              .map(Category.getId);

            const subCategoryPath = intersperse("subCategories", subCategories);
            const subCategoryCategoryPath = [...subCategoryPath, "category"];
            const subCategoryReferencesPath = [
              ...subCategoryPath,
              "materialReferences"
            ];

            const subCategoryLens = lensPath(subCategoryCategoryPath);
            const subReferencesLens = lensPath(subCategoryReferencesPath);

            let byCategory = mergeDeepLeft(
              categories,
              subCategoriesPathToNested(subCategories)
            );

            byCategory = ensureParentCategories(
              byCategory,
              category,
              subCategories,
              subCategoryTitles
            );

            if (
              isEmpty(view(subCategoryLens, byCategory)) &&
              isEmpty(view(subReferencesLens, byCategory))
            ) {
              byCategory = set(
                subCategoryLens,
                {
                  title: subCategoryTitle.replace(/\*$/, ""),
                  fullTitle: Category.trimFullTitle(category.title),
                  type: category.type,
                  view: category.view
                },
                byCategory
              );
            }

            byCategory = over(
              subReferencesLens,
              append({
                id: material._id,
                materialId: material.materialId,
                sorting: category.sorting,
                pages: category.pages,
                book: material.book,
                istTitel: material.istTitel
              }),
              byCategory
            );

            return byCategory;
          } else {
            if (categories[id]) {
              categories[id].materialReferences.push({
                id: material._id,
                materialId: material.materialId,
                sorting: category.sorting,
                pages: category.pages,
                book: material.book,
                istTitel: material.istTitel
              });
            } else {
              categories[id] = {
                category: {
                  title: category.title,
                  fullTitle: Category.trimFullTitle(category.title),
                  type: category.type,
                  view: category.view
                },
                materialReferences: [
                  {
                    id: material._id,
                    materialId: material.materialId,
                    sorting: category.sorting,
                    pages: category.pages,
                    book: material.book,
                    istTitel: material.istTitel
                  }
                ],
                subCategories: {}
              };
            }
          }

          return categories;
        },
        {
          "Eigene Materialien_page_list": {
            category: {
              isStatic: true,
              kind: "custom_materials",
              title: "Eigene Materialien",
              fullTitle: "Eigene Materialien",
              type: "page",
              view: "list"
            },
            materialReferences: [],
            subCategories: {}
          },
          "Eigene Materialien_book_list": {
            category: {
              isStatic: true,
              kind: "custom_materials",
              title: "Eigene Materialien",
              fullTitle: "Eigene Materialien",
              type: "book",
              view: "list"
            },
            materialReferences: [],
            subCategories: {}
          },
          Präsentationsmodus_page_list: {
            category: {
              isStatic: true,
              kind: "presentation_materials",
              title: "Präsentationsmodus",
              fullTitle: "Präsentationsmodus",
              type: "page",
              view: "list"
            },
            materialReferences: [],
            subCategories: {}
          },
          Präsentationsmodus_book_list: {
            category: {
              isStatic: true,
              kind: "presentation_materials",
              title: "Präsentationsmodus",
              fullTitle: "Präsentationsmodus",
              type: "book",
              view: "list"
            },
            materialReferences: [],
            subCategories: {}
          }
        }
      )
    )(materialsList);

    if (!showPresentationMode) {
      return Object.values(
        omit(
          ["Präsentationsmodus_page_list", "Präsentationsmodus_book_list"],
          categoriesWithMaterials
        )
      );
    }

    return Object.values(categoriesWithMaterials);
  }
);

export const selectCategoriesWithMaterialsByTab = createSelector(
  selectCategoriesWithMaterials,
  selectDlsActiveTab,
  selectDisplayedPages,
  (categoriesWithMaterials, tab, pages) => {
    let isValidCategoryType = () => false;

    if (tab === "all") {
      isValidCategoryType = propEq("type", "book");
    } else if (tab === "page") {
      isValidCategoryType = propEq("type", "page");
    }

    const prepareCategories = categories => {
      return categories
        .filter(c => isValidCategoryType(c.category))
        .map(c => {
          const references = c.materialReferences.filter(ref => {
            if (c.category.type === "book") {
              if (
                c.category.kind &&
                c.category.kind === "presentation_materials"
              ) {
                return Boolean(ref.book);
              }
              return true;
            } else if (c.category.type === "page") {
              const materialPages = ref.pages.map(page =>
                relativePageToAbsolute(page)
              );
              return intersection(pages, materialPages).length > 0;
            } else {
              return false;
            }
          });

          return {
            ...c.category,
            references,
            count: references.filter(ref => !ref.istTitel).length,
            subCategories: prepareCategories(Object.values(c.subCategories))
          };
        });
    };

    return prepareCategories(categoriesWithMaterials);
  }
);

export const selectCategories = createSelector(
  selectCategoriesWithMaterialsByTab,
  selectFixedCategoryOrder,
  selectDlsActiveTab,
  (categories, fixedCategories, tab) => {
    let isValidCategoryType = () => false;

    if (tab === "all") {
      isValidCategoryType = propEq("type", "book");
    } else if (tab === "page") {
      isValidCategoryType = propEq("type", "page");
    }

    // compare by position in catsort array
    const fixedIndexComparator = (a, b) => {
      const isTitleA = isEqualNoCase(prop("title", a));
      const isTitleB = isEqualNoCase(prop("title", b));

      return (
        findIndex(isTitleA, fixedCategories) -
        findIndex(isTitleB, fixedCategories)
      );
    };

    const isFixedCategory = category => {
      const title = propOr("", "title", category);
      return includes(toLower(title), fixedCategories);
    };

    const isCommonCategory = complement(isFixedCategory);

    const isStaticCategory = category =>
      category.title === "Eigene Materialien" ||
      category.title === "Präsentationsmodus";

    const isNonStaticCategory = complement(isStaticCategory);

    const filterCommonCategories = filter(
      both(isValidCategoryType, both(isCommonCategory, isNonStaticCategory))
    );

    const filterFixedCategories = filter(
      both(isValidCategoryType, both(isFixedCategory, isNonStaticCategory))
    );

    const filterStaticCategories = filter(
      both(isValidCategoryType, isStaticCategory)
    );

    const filterCats = cats => {
      cats.forEach(cat => (cat.subCategories = filterCats(cat.subCategories)));

      const filtered = filterCommonCategories(cats);
      const fixedFiltered = filterFixedCategories(cats);
      const staticFiltered = filterStaticCategories(cats);

      return unnestAll(
        sort(fixedIndexComparator, fixedFiltered),
        sortBy(prop("title"), filtered),
        staticFiltered
      );
    };

    return filterCats(categories);
  }
);

export const selectCurrentDlsCategory = createSelector(
  selectCategories,
  selectDlsActivePageCategory,
  selectDlsActiveBookCategory,
  selectDlsActiveTab,
  (categories, pageCategoryTitle, bookCategoryTitle, tab) => {
    let typePredicate = () => false;
    let titlePredicate = () => false;

    if (tab === "all") {
      typePredicate = propEq("type", "book");
      titlePredicate = propEq("title", bookCategoryTitle);
    } else if (tab === "page") {
      typePredicate = propEq("type", "page");
      titlePredicate = propEq("title", pageCategoryTitle);
    }

    const findCategory = categories => {
      for (let category of categories) {
        if (both(typePredicate, titlePredicate)(category)) {
          return category;
        }
        const subCategory = findCategory(category.subCategories);
        if (subCategory) return subCategory;
      }
      return null;
    };

    return findCategory(categories);
  }
);

export const selectMaterialsByCategory = createSelector(
  selectCategories,
  selectMergedMaterials,
  selectCurrentDlsCategory,
  (categories, materials, category) => {
    const findCategory = categories => {
      for (let cat of categories) {
        if (Category.isEqual(cat, category)) {
          return cat;
        }
        const subCategory = findCategory(cat.subCategories);
        if (subCategory) return subCategory;
      }
      return null;
    };

    const cat = findCategory(categories);
    const refIds = cat
      ? cat.references.map(ref => {
          if (category.kind && category.kind === "presentation_materials") {
            return ref.materialId;
          }
          return ref.id;
        })
      : [];

    const materialsByCategory = Object.values(pick(refIds, materials));
    return materialsByCategory.map(material => {
      const activeCategory = material.categories.find(
        cat => cat.title === category.title
      );
      return {
        ...material,
        sorting: activeCategory ? activeCategory.sorting : material.sorting
      };
    });
  }
);

export const selectMaterialsWithNoteAndPresentationMarker = createSelector(
  selectMergedMaterials,
  selectNotes,
  selectPresentationMarkers,
  (materials, notes, markers) =>
    Object.values(materials).map(material => ({
      ...material,
      note: find(propEq("materialId", material._id))(notes),
      presentationMarker: find(propEq("materialId", material._id))(markers)
    }))
);

export const selectMaterialsByCategoryWithNoteAndPresentationMarker = createSelector(
  selectMaterialsByCategory,
  selectNotes,
  selectPresentationMarkers,
  (materials, notes, markers) =>
    materials.map(material => ({
      ...material,
      note: find(propEq("materialId", material._id))(notes),
      presentationMarker: find(propEq("materialId", material._id))(markers)
    }))
);

export const selectMaterialsByCategoryAndTabSorted = createSelector(
  selectMaterialsByCategoryWithNoteAndPresentationMarker,
  sortBy(prop("sorting"))
);

export const selectMaterialById = id =>
  createSelector(
    selectMaterialsWithNoteAndPresentationMarker,
    find(propEq("_id", id))
  );

export const selectMarkedMaterials = createSelector(
  selectPresentationMarkers,
  selectMergedMaterials,
  (markers, materials) => {
    const markedMaterials = pipe(
      filter(marker => marker.book || marker.pages.length > 0),
      map(marker => {
        return {
          marker,
          material: materials[marker.materialId]
        };
      }),
      chain(markedMaterial => {
        if (markedMaterial.marker.pages.length === 0) {
          return {
            ...markedMaterial.material,
            position: {}
          };
        }

        return markedMaterial.marker.pages.map(page => {
          return {
            ...markedMaterial.material,
            position: {
              page
            }
          };
        });
      }),
      groupBy(pathOr(NON_ASSIGNED_PAGE, ["position", "page"]))
    )(markers);

    return markedMaterials;
  }
);

export const selectMarkedMaterialsByPages = createSelector(
  selectMarkedMaterials,
  selectDisplayedPagesOnRightSidebar,
  (markedMaterials, pages) => {
    if (isEmpty(pages)) {
      return markedMaterials;
    }

    return pick([...pages, NON_ASSIGNED_PAGE], markedMaterials);
  }
);

export const selectMarkedMaterialsSize = createSelector(
  selectMarkedMaterialsByPages,
  pipe(map(length), values, reduce(add, 0))
);

export const selectPagelessMarkedMaterials = createSelector(
  selectMarkedMaterials,
  propOr([], NON_ASSIGNED_PAGE)
);
